import { useEffect, useRef, useState } from "react";
import { useSession } from "../hooks";
import { navigate, parse } from "../router";
import type { Session } from "../session";
import { IconBack, IconClock, IconForward, IconHelp, IconSearch } from "./icons";
import { PopMenu } from "./PopMenu";
import { modKey } from "./Shortcuts";
import { t } from "../i18n";
import { pageLabel } from "./SettingsNav";

const pages = (): Record<string, string> => ({
  "/inbox": t("Inbox"),
  "/unreads": t("All unreads"),
  "/threads": t("Threads"),
  "/saved": t("Saved for later"),
  "/scheduled": t("Scheduled messages"),
  "/drafts": t("Drafts & sent"),
  "/emoji": t("Custom emoji"),
  "/help": t("Help"),
  "/people": t("People"),
  "/search": t("Search"),
  "/tasks": t("Tasks"),
  "/new": t("New message"),
  "/fleet": t("Fleet"),
  "/workflows": t("Workflows"),
});

// placeName is what a place in the app is called in the history list.
function placeName(session: Session, path: string): string {
  const r = parse(path);
  if (r.channel) return session.channelName(r.channel);
  if (r.settings !== undefined || r.admin !== undefined) {
    const admin = r.admin !== undefined;
    const page = pageLabel(admin, r.admin ?? r.settings ?? "");
    const where = admin ? t("Administration") : t("Settings");
    return page ? t("{place}: {page}", { place: where, page }) : where;
  }
  const names = pages();
  const page = Object.keys(names).find((p) => path === p || path.startsWith(p + "/") || path.startsWith(p + "?"));
  return page ? names[page] : path;
}

type Navigation = { canGoBack?: boolean; canGoForward?: boolean };

// TopBar runs across the top of the window: back and forward, the places
// one went lately, search or jump to anything, and help.
export function TopBar({ path, onSearch }: { path: string; onSearch: () => void }) {
  const session = useSession();
  const recent = useRef<string[]>([]);
  const [recentMenu, setRecentMenu] = useState<DOMRect | null>(null);
  useEffect(() => {
    const at = path.replace(/\/m\/[0-9a-f-]{36}$/, "");
    recent.current = [at, ...recent.current.filter((p) => p !== at)].slice(0, 12);
  }, [path]);
  const nav = (window as unknown as { navigation?: Navigation }).navigation;

  return (
    <header className="topbar">
      <span className="topbar-nav">
        <button className="topbar-tool" onClick={() => window.history.back()} disabled={nav?.canGoBack === false} aria-label={t("Back")} title={t("Back")}>
          <IconBack size={17} />
        </button>
        <button className="topbar-tool" onClick={() => window.history.forward()} disabled={nav?.canGoForward === false} aria-label={t("Forward")} title={t("Forward")}>
          <IconForward size={17} />
        </button>
        <button
          className="topbar-tool"
          aria-label={t("History")}
          title={t("History")}
          aria-haspopup="menu"
          onClick={(e) => setRecentMenu(recentMenu ? null : e.currentTarget.getBoundingClientRect())}
        >
          <IconClock size={17} />
        </button>
      </span>
      <button className="topbar-search" onClick={onSearch} aria-label={t("Search or jump to")}>
        <IconSearch size={15} />
        <span>{t("Search messages, or jump to a channel, person or page")}</span>
        <kbd>{modKey}K</kbd>
      </button>
      <span className="topbar-end">
        <button className="topbar-tool" onClick={() => navigate("/help")} aria-label={t("Help")} title={t("Help")}>
          <IconHelp size={18} />
        </button>
      </span>
      {recentMenu && (
        <PopMenu
          anchor={recentMenu}
          label={t("Recently visited")}
          onClose={() => setRecentMenu(null)}
          items={[
            { label: t("Recently visited"), heading: true },
            ...recent.current.slice(1).map((p) => ({ label: placeName(session, p), run: () => navigate(p) })),
            ...(recent.current.length < 2 ? [{ label: t("Places you visit show here"), heading: true }] : []),
          ]}
        />
      )}
    </header>
  );
}
